import { useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Btn, F, PageHeader, Toggle } from "../../components/admin/ui";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { ConfirmDelete } from "./Products";

export const PERMS = [
  ["reports", "لوحة التحكم والتقارير"], ["orders", "الطلبات"], ["products", "المنتجات والمخزون ورابط البوت"],
  ["categories", "التصنيفات"], ["coupons", "الكوبونات"], ["customers", "العملاء والقائمة البريدية"],
  ["shipping", "الشحن"], ["content", "محتوى الرئيسية"], ["settings", "الإعدادات"], ["finance", "الأرباح والمحفظة"],
];
const LABEL = Object.fromEntries(PERMS);
const EMPTY = { name: "", email: "", password: "", permissions: ["orders"], active: true };
const ERR = { email_exists: "البريد مستخدم مسبقًا", weak_password: "كلمة المرور 8 أحرف على الأقل", invalid_email: "بريد غير صحيح", invalid_name: "أدخل الاسم" };

export default function Staff() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const togglePerm = (p) => setEdit((e) => ({ ...e, permissions: e.permissions.includes(p) ? e.permissions.filter((x) => x !== p) : [...e.permissions, p] }));

  const save = async () => {
    const body = { name: edit.name, email: edit.email, password: edit.password || null, permissions: edit.permissions, active: edit.active };
    try {
      if (edit.admin_id) await api.put(`/admin/staff/${edit.admin_id}`, body);
      else await api.post("/admin/staff", body);
      toast.success("تم حفظ حساب الموظف");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(ERR[e?.response?.data?.detail] || errText(e));
    }
  };

  return (
    <div data-testid="admin-staff">
      <PageHeader title="الموظفون والصلاحيات" desc="أضف حسابات للموظفين وحدد الأقسام التي يستطيعون الوصول إليها. يدخل الموظف من نفس صفحة دخول الإدارة.">
        <Btn data-testid="add-staff-button" onClick={() => setEdit(EMPTY)}><Plus size={16} />إضافة موظف</Btn>
      </PageHeader>
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500"><tr className="text-right"><th className="p-3">الاسم</th><th>البريد</th><th>النوع</th><th>الصلاحيات</th><th>الحالة</th><th></th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.admin_id} data-testid={`staff-row-${a.email}`} className="border-t border-gray-100">
                <td className="p-3 font-medium">{a.name}</td>
                <td dir="ltr" className="text-right text-xs">{a.email}</td>
                <td>{a.role === "owner" ? <span className="rounded bg-ink px-2 py-0.5 text-xs text-white">مالك</span> : "موظف"}</td>
                <td className="max-w-xs text-xs text-gray-600">{a.role === "owner" ? "كل الصلاحيات" : (a.permissions || []).map((p) => LABEL[p]).join("، ") || "—"}</td>
                <td><span className={`rounded px-2 py-0.5 text-xs ${a.active !== false ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{a.active !== false ? "مفعل" : "موقوف"}</span></td>
                <td className="p-3">{a.role !== "owner" && (
                  <div className="flex justify-end gap-1">
                    <button data-testid={`edit-staff-${a.email}`} onClick={() => setEdit({ ...a, password: "" })} className="rounded p-1.5 hover:bg-gray-100"><Pencil size={15} /></button>
                    <ConfirmDelete testid={`delete-staff-${a.email}`} onConfirm={async () => { await api.delete(`/admin/staff/${a.admin_id}`); load(); }} />
                  </div>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent dir="rtl" className="max-w-lg">
          <DialogTitle className="pr-8">{edit?.admin_id ? "تعديل الموظف" : "موظف جديد"}</DialogTitle>
          {edit && (
            <div className="space-y-3">
              <F label="الاسم" testid="staff-name-input" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} />
              <F label="البريد الإلكتروني (للدخول)" dir="ltr" testid="staff-email-input" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} />
              <F label={edit.admin_id ? "كلمة مرور جديدة (اتركها فارغة بدون تغيير)" : "كلمة المرور (8 أحرف على الأقل)"} type="password" dir="ltr" testid="staff-password-input" value={edit.password} onChange={(v) => setEdit({ ...edit, password: v })} />
              <div>
                <p className="mb-2 text-xs text-gray-600">الصلاحيات</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {PERMS.map(([p, l]) => (
                    <label key={p} className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                      <Checkbox data-testid={`staff-perm-${p}`} checked={edit.permissions.includes(p)} onCheckedChange={() => togglePerm(p)} />{l}
                    </label>
                  ))}
                </div>
              </div>
              <Toggle label="الحساب مفعل" testid="staff-active-toggle" checked={edit.active !== false} onChange={(v) => setEdit({ ...edit, active: v })} />
              <Btn data-testid="save-staff-button" className="w-full" onClick={save}>حفظ</Btn>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
